import { useQuery } from "@/api/utils/useQuery"
import { graphqlClient } from "@/api/hasura/client"
import { QueryResult } from "@/api/utils/types"
import {
  Financials_Warrants_Communication_Group_Bool_Exp,
  GetWarrantsCommunicationGroupsDocument,
  GetWarrantsCommunicationGroupsQuery,
  GetWarrantsCommunicationsDocument,
} from "@/api/hasura/generated/graphql"
import { WarrantsAccountState } from "@/models/admin/warrantsAccount/warrantsAccount.types"
import {
  WarrantsCompletionSummaryParser,
  WarrantsCompletionSummaryRaw,
  WarrantsPhaseGroup,
} from "@/models/admin/warrantsCompletionSummary/warrantsCompletionSummary.parser"
import { IWarrantsCompletionSummary } from "@/models/admin/warrantsCompletionSummary/warrantsCompletionSummary.types"

const EMPTY_SUMMARY: IWarrantsCompletionSummary = {
  totalCommunications: 0,
  totalCompleted: 0,
  averageDaysToComplete: null,
}

// The dataset is small (hundreds of rows); a generous page size fetches it all.
const PAGE_SIZE = 10000

function toPhaseGroups(
  data: GetWarrantsCommunicationGroupsQuery,
): WarrantsPhaseGroup[] {
  return data.financials_warrants_communication_group.map((group) => ({
    accountNumber: group.account_number,
    createdAt: group.created_at,
  }))
}

class WarrantsSummaryServiceClass {
  /**
   * Summary for the /warrants page, scoped to an optional date window (ISO
   * strings): total communications, count of completed journeys, and the average
   * time a completed journey took.
   *
   * A new communication group is created per communication and tagged with the
   * account's warrant phase (`parent_status`). A "completed journey" is an account
   * that reached `warrant_phase_tenth` in the window; its duration is measured from
   * that account's `warrant_phase_first` group `created_at` to its
   * `warrant_phase_tenth` group `created_at`. The window scopes journeys by their
   * completion (tenth-phase date); the first-phase lookup is not date-bounded, so
   * the full journey length is captured even when it started before the window.
   */
  getCompletionSummary(
    from?: string,
    to?: string,
  ): QueryResult<IWarrantsCompletionSummary> {
    const dateBounds =
      from && to ? { created_at: { _gte: from, _lte: to } } : {}

    const tenthWhere: Financials_Warrants_Communication_Group_Bool_Exp = {
      parent_status: { _eq: WarrantsAccountState.WARRANT_PHASE_TENTH },
      ...dateBounds,
    }

    return useQuery({
      queryKey: ["warrantsCompletionSummary", from ?? null, to ?? null],
      useRawResponse: true,
      placeholderData: EMPTY_SUMMARY,
      queryFn: async (): Promise<WarrantsCompletionSummaryRaw> => {
        // Communications volume in the window (count only — limit 0 keeps it lean).
        const commsCountPromise = graphqlClient.request(
          GetWarrantsCommunicationsDocument,
          { offset: 0, limit: 0, where: dateBounds },
        )
        // Accounts that reached the final phase in the window.
        const tenthPromise = graphqlClient.request(
          GetWarrantsCommunicationGroupsDocument,
          { offset: 0, limit: PAGE_SIZE, where: tenthWhere },
        )

        const [commsCount, tenth] = await Promise.all([
          commsCountPromise,
          tenthPromise,
        ])

        const tenthPhaseGroups = toPhaseGroups(tenth)
        const accountNumbers = [
          ...new Set(tenthPhaseGroups.map((group) => group.accountNumber)),
        ]

        // Their journey start — any date, so long journeys aren't truncated.
        const first = accountNumbers.length
          ? await graphqlClient.request(
              GetWarrantsCommunicationGroupsDocument,
              {
                offset: 0,
                limit: PAGE_SIZE,
                where: {
                  parent_status: {
                    _eq: WarrantsAccountState.WARRANT_PHASE_FIRST,
                  },
                  account_number: { _in: accountNumbers },
                },
              },
            )
          : null

        return {
          totalCommunications:
            commsCount.financials_warrants_communications_aggregate.aggregate
              ?.count ?? 0,
          tenthPhaseGroups,
          firstPhaseGroups: first ? toPhaseGroups(first) : [],
        }
      },
      parserFn: WarrantsCompletionSummaryParser.toDomain,
    })
  }
}

export const WarrantsSummaryService = new WarrantsSummaryServiceClass()
